/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * PassCheckInBridge — self check-in for the pass cards in My Registrations.
 *
 * The cards are a Page Builder section: plain HTML and script inside a frame,
 * with no React, no session helpers and no check-in rules of their own. This
 * component is the React half that sits beside it on the page:
 *
 *   frame → here   PASS_MSG.HELLO    {passIds}  the cards on screen
 *                  PASS_MSG.CHECKIN  {passId}   a card's button was pressed
 *   here → frame   PASS_MSG.STATE    {passes}   per pass: its activity name,
 *                                               status and button (passCardState)
 *
 * and, on a press, it opens the same check-in dialog the event page used to
 * show, for that one pass. The frame only ever draws what it is told; every
 * decision — whether the window is open, what the button says, what is written
 * — is made here, by the same rules module the dialog uses.
 *
 * THE EVENT IS READ FROM ITS OWN PUBLIC ROW. A pass's related `event` carries a
 * title and a start, but the window needs the status and the end as well, and
 * the activity names come with that row too. Only the events of the cards on
 * screen are read, a few at a time, so a long list costs nothing extra.
 *
 * Messages are accepted only from a frame that is actually on this page, and a
 * press only ever names a pass id: the dialog is opened for a pass from this
 * visitor's own owner-scoped list, so a frame cannot point it at anybody else's.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useMyPasses from '../../../hooks/useMyPasses';
import useSelfCheckInWrite from '../../../hooks/useSelfCheckInWrite';
import { fetchPublicRecordDetail } from '../../../hooks/usePublicTableView';
import { buildRelatedScope } from '../../../utils/relatedScope';
import { isUserLoggedIn } from '../../../utils/utils';
import { idOf } from '../../organizer/checkin/checkInRules';
import { passCardState } from '../event-detail/selfCheckInRules';
import { fetchRegistrationActivities } from '../event-detail/fetchMyRegistrations';
import SelfCheckIn, { CHECK_IN_BUTTON, CHECK_OUT_BUTTON } from '../event-detail/SelfCheckIn';

export const PASS_MSG = {
  HELLO: 'ATHLO_PASSES_HELLO',
  CHECKIN: 'ATHLO_PASS_CHECKIN',
  STATE: 'ATHLO_PASSES_STATE',
};

const EVENT_SLUG = 'event';
/** Events read at once — under the public API's burst allowance. */
const PARALLEL_READS = 3;
/** Gaps between re-reads of the passes while cards on screen are missing from them. */
const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000];
/** The buttons follow the clock: a window that opens while the page is open. */
const TICK_MS = 60 * 1000;

/** The frames on this page, so a message can be checked against them. */
function pageFrames() {
  return Array.from(document.querySelectorAll('iframe'))
    .map((frame) => frame.contentWindow)
    .filter(Boolean);
}

/** The activity a pass is for, from its event's activities and its registration. */
export function activityNameFor(pass, event, activities, regActivities) {
  if (!pass || !event) return '';
  const list = Array.isArray(activities) ? activities : [];
  const reg = idOf(pass.registration_id);
  const activityId = reg != null ? regActivities?.[String(reg)] : undefined;
  const named = activityId != null
    ? list.find((activity) => String(activity?.id) === String(activityId)) : null;
  // The activity the seat was BOOKED for comes first, whatever the event's
  // mode: a sequential event registered activity by activity carries each
  // one on its registration, and "All legs" there hid the real name.
  if (named && typeof named.name === 'string') return named.name.trim();
  // Only a sequential entry with no activity of its own covers every leg.
  if (String(event.activity_mode || '').toUpperCase() === 'SEQUENTIAL') return 'All legs';
  // One activity: there is nothing else it could be.
  if (list.length === 1 && typeof list[0]?.name === 'string') return list[0].name.trim();
  return '';
}

export default function PassCheckInBridge() {
  const { passes, reload } = useMyPasses(true);
  // event id → { record, activities } once read.
  const [events, setEvents] = useState({});
  const [regActivities, setRegActivities] = useState({});
  const [wanted, setWanted] = useState([]);        // pass ids on screen
  const [openPassId, setOpenPassId] = useState(null);
  const [now, setNow] = useState(() => new Date());
  const framesRef = useRef(new Set());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const byId = useMemo(() => {
    const map = {};
    (Array.isArray(passes) ? passes : []).forEach((pass) => {
      if (pass?.id != null) map[String(pass.id)] = pass;
    });
    return map;
  }, [passes]);

  // ASK AGAIN WHILE A CARD ON SCREEN IS NOT IN THE LIST. Straight after a page
  // reload the sign-in can still be settling (a token being refreshed), and
  // useMyPasses then hears "nobody is signed in" and keeps that empty answer —
  // while the cards, read a moment later through the page's own binding, do
  // arrive. Nothing would ever ask again, so the cards stayed without a button
  // until somebody navigated away and back. A new page or search of cards
  // starts the backoff over.
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const [attempt, setAttempt] = useState(0);
  const unknown = wanted.some((passId) => !byId[String(passId)]);
  useEffect(() => { setAttempt(0); }, [wanted]);
  useEffect(() => {
    if (!unknown || attempt >= RETRY_DELAYS_MS.length) return undefined;
    const timer = setTimeout(() => {
      setAttempt((n) => n + 1);
      reloadRef.current();
    }, RETRY_DELAYS_MS[attempt]);
    return () => clearTimeout(timer);
  }, [unknown, attempt]);

  // The events of the cards on screen, read a few at a time.
  const eventIdsWanted = useMemo(() => {
    const ids = new Set();
    wanted.forEach((passId) => {
      const pass = byId[String(passId)];
      const eventId = pass ? idOf(pass.event_id) : null;
      if (eventId != null) ids.add(String(eventId));
    });
    return Array.from(ids);
  }, [wanted, byId]);

  // Events already asked for (in flight or answered), kept OUTSIDE state and
  // never cancelled: a re-render mid-read (another hello, the passes arriving)
  // must not abandon a read that `events` would then treat as under way for
  // ever — which is exactly how every card stayed without its activity and
  // button. A failed read is forgotten, so a later render asks again.
  const requestedRef = useRef(new Set());
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  useEffect(() => {
    const queue = eventIdsWanted.filter((id) => !requestedRef.current.has(id));
    if (!queue.length) return;
    queue.forEach((id) => requestedRef.current.add(id));
    const worker = async () => {
      while (queue.length) {
        const id = queue.shift();
        try {
          // eslint-disable-next-line no-await-in-loop
          const data = await fetchPublicRecordDetail(EVENT_SLUG, id);
          const scope = buildRelatedScope(data?.related);
          const entry = {
            record: data?.record || null,
            activities: Array.isArray(scope.categories) ? scope.categories : [],
          };
          if (aliveRef.current) setEvents((all) => ({ ...all, [id]: entry }));
        } catch (e) {
          // Unreadable for now: no button rather than a wrong one, and asked
          // again the next time the cards on screen change.
          requestedRef.current.delete(id);
        }
      }
    };
    Array.from({ length: Math.min(PARALLEL_READS, queue.length) }, worker);
  }, [eventIdsWanted]);

  // Which activity each registration is for — asked once per set of passes.
  const regKey = useMemo(() => (Array.isArray(passes) ? passes : [])
    .map((pass) => idOf(pass?.registration_id))
    .filter((id) => id !== null && id !== undefined && id !== '')
    .map(String).sort().join(','), [passes]);
  useEffect(() => {
    if (!regKey) return undefined;
    let cancelled = false;
    isUserLoggedIn()
      .then((who) => (who?.user_id ? fetchRegistrationActivities({
        applicationUserId: who.user_id, registrationIds: regKey.split(','),
      }) : null))
      .then((map) => { if (!cancelled && map) setRegActivities(map); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [regKey]);

  // What every known card should draw.
  const cardStates = useMemo(() => {
    const out = {};
    Object.values(byId).forEach((pass) => {
      const entry = events[String(idOf(pass.event_id))];
      const record = entry?.record || null;
      out[String(pass.id)] = {
        status: String(pass.status || '').toUpperCase(),
        activity: activityNameFor(pass, record, entry?.activities, regActivities),
        checkIn: passCardState(pass, record, now),
      };
    });
    return out;
  }, [byId, events, regActivities, now]);

  const statesRef = useRef(cardStates);
  statesRef.current = cardStates;

  const send = useCallback((target) => {
    const message = { type: PASS_MSG.STATE, passes: statesRef.current };
    const targets = target ? [target] : Array.from(framesRef.current);
    targets.forEach((frame) => {
      try { frame.postMessage(message, '*'); } catch (e) { /* frame gone */ }
    });
  }, []);

  useEffect(() => { send(); }, [cardStates, send]);

  useEffect(() => {
    function onMessage(e) {
      const type = e.data && e.data.type;
      if (type !== PASS_MSG.HELLO && type !== PASS_MSG.CHECKIN) return;
      if (!e.source || !pageFrames().includes(e.source)) return;
      framesRef.current.add(e.source);

      if (type === PASS_MSG.HELLO) {
        const ids = Array.isArray(e.data.passIds) ? e.data.passIds.map(String) : [];
        setWanted((current) => {
          const merged = Array.from(new Set([...current, ...ids]));
          return merged.length === current.length ? current : merged;
        });
        send(e.source);
        return;
      }
      // A press. Only a pass from this visitor's own list, and only while its
      // card offers something to do.
      const passId = String(e.data.passId || '');
      const state = statesRef.current[passId];
      if (state && state.checkIn && state.checkIn.enabled) setOpenPassId(passId);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [send]);

  const openPass = openPassId ? byId[openPassId] : null;
  const openEntry = openPass ? events[String(idOf(openPass.event_id))] : null;
  const openEvent = openEntry?.record || null;

  const write = useSelfCheckInWrite({ eventId: openEvent?.id, reload });
  const { clearError } = write;
  const close = useCallback(() => { setOpenPassId(null); clearError(); }, [clearError]);

  if (!openPass || !openEvent) return null;

  return (
    <SelfCheckIn
      open
      onClose={close}
      event={openEvent}
      eventId={openEvent.id}
      // Just this card's pass: the dialog is for the seat that was pressed.
      passes={[openPass]}
      now={now}
      activityNames={{ [String(openPass.id)]: cardStates[String(openPass.id)]?.activity || '' }}
      busy={write.busy}
      error={write.error}
      onCheckIn={({ userInfo, passId }) => write.run(CHECK_IN_BUTTON, { userInfo, passId })}
      onCheckOut={({ passId }) => write.run(CHECK_OUT_BUTTON, { passId })}
    />
  );
}
